import React, { useState } from 'react';
import { X, AlertCircle, AlertTriangle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';

/**
 * AlertBar - Renders header alerts (top center)
 *
 * Types: error, warning, confirm (success moved to footer)
 * 1 alert: renders normally
 * 2+ alerts: collapses to summary row (expanded by default), Dismiss All button
 */

const TYPE_PRIORITY = { error: 4, confirm: 3, warning: 2, info: 1 };

const getIcon = (type, size = 18) => {
  switch (type) {
    case 'error':   return <AlertCircle size={size} />;
    case 'warning': return <AlertTriangle size={size} />;
    case 'confirm': return <AlertTriangle size={size} />;
    case 'info':
    default:        return <Info size={size} />;
  }
};

const getWorstType = (alerts) => {
  return alerts.reduce((worst, a) => {
    return (TYPE_PRIORITY[a.type] || 0) > (TYPE_PRIORITY[worst] || 0) ? a.type : worst;
  }, 'info');
};

const AlertBar = () => {
  const { alerts, dismissAlert, notify } = useNotifications();
  const clearAlerts = notify.clearAlerts;
  const [isExpanded, setIsExpanded] = useState(false);

  if (alerts.length === 0) return null;

  const renderAlertItem = (alert) => (
    <div key={alert.id} className={`stui-alert-item stui-alert-${alert.type}`}>
      <span className="stui-alert-icon">{getIcon(alert.type)}</span>
      <span className="stui-alert-message">{alert.message}</span>
      {alert.type === 'confirm' ? (
        <div className="stui-alert-actions">
          <button className="stui-btn stui-btn-primary" onClick={() => { alert.onConfirm?.(); dismissAlert(alert.id); }}>
            {alert.confirmText}
          </button>
          <button className="stui-btn stui-btn-secondary" onClick={() => { alert.onCancel?.(); dismissAlert(alert.id); }}>
            {alert.cancelText}
          </button>
        </div>
      ) : (
        <button className="stui-alert-dismiss" onClick={() => dismissAlert(alert.id)} title="Dismiss">
          <X size={16} />
        </button>
      )}
    </div>
  );

  // Single alert — render normally
  if (alerts.length === 1) {
    return (
      <div className="stui-alert-bar">
        {renderAlertItem(alerts[0])}
      </div>
    );
  }

  // 2+ alerts — replace everything with collapsed stack indicator
  const worstType = getWorstType(alerts);
  return (
    <div className="stui-alert-bar">
      <div className={`stui-alert-item stui-alert-summary stui-alert-${worstType}`}>
        <span className="stui-alert-icon">{getIcon(worstType)}</span>
        <span className="stui-alert-message stui-alert-summary-label">
          {alerts.length} alerts
        </span>
        <div className="stui-alert-summary-actions">
          <button className="stui-alert-btn-dismiss-all" onClick={clearAlerts}>
            Dismiss All
          </button>
          <button className="stui-alert-dismiss" onClick={() => setIsExpanded(e => !e)} title={isExpanded ? 'Collapse' : 'Expand'}>
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="stui-alert-stack">
          {alerts.map(renderAlertItem)}
        </div>
      )}
    </div>
  );
};

export default AlertBar;
